import http from '../http';
import { observable, action, makeObservable, runInAction, computed } from "mobx";

class OrdersStore {
    orders = [];
    ordersDeleted = [];
    isLoading = false;
    isLoadingDeleted = false;

    page = 1;
    itemsPerPage = 10;

    currentOrder = null;
    currentSelections = [];
    currentPictures = [];
    currentTags = [];
    shareDialogData = null;

    currentSelection = null;
    selectionPictures = [];
    selectionTags = [];

    currentOrderStatus = null;
    currentExpiryStatus = null;
    searchType = null;     // "name" / "phone", null = no search
    searchText = null;
    fetchOrdersRequestId = 0;

    pagination = {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
        hasNextPage: false,
        hasPrevPage: false
    };

    stats = {
        totalOrders: 0,
        totalPhotos: 0,
        totalSelected: 0,
        expiredCount: 0,
        statusCounts: null,
        expiryCounts: null,
    };

    constructor() {
        makeObservable(this, {
            orders: observable,
            ordersDeleted: observable,
            isLoading: observable,
            isLoadingDeleted: observable,
            currentOrder: observable,
            currentSelections: observable,
            currentPictures: observable,
            currentTags: observable,
            shareDialogData: observable,
            currentSelection: observable,
            selectionPictures: observable,
            selectionTags: observable,
            page: observable,
            pagination: observable,
            stats: observable,
            currentOrderStatus: observable,
            currentExpiryStatus: observable,
            searchType: observable,
            searchText: observable,
            fetchOrders: action,
            deleteOrder: action,
            setPage: action,
            totalPage: computed,
            fetchOrdersDeleted: action,
        });
    }

    setPage = (newPage) => {
        this.page = newPage;
        this.fetchOrders();
    }

    get totalPage() {
        return this.pagination.totalPages;
    }

    updateCurrentOrderField = (fieldName, value) => {
        if (this.currentOrder) {
            this.currentOrder = {
                ...this.currentOrder,
                [fieldName]: value
            };
        }
    }

    setShareDialogData = (data) => {
        this.shareDialogData = data;
    }

    fetchOrder(orderId) {
        return http.get(`/user/order/${orderId}`)
            .then(response => {
                runInAction(() => {
                    this.currentOrder = {
                        ...response.data.order,
                        tagDefinitions: response.data.currentTagDefinitions
                            || response.data.order?.tagDefinitions
                            || []
                    }
                    this.currentSelections = response.data.selections || [];
                    this.currentPictures = response.data.pictures || [];
                    this.currentTags = response.data.tags || [];
                });
            })
            .catch(error => {});
    }

    fetchOrdersDeleted = () => {
        this.isLoadingDeleted = true;
        http.get('/user/orders/deleted')
            .then(response => {
                runInAction(() => {
                    this.ordersDeleted = response.data;
                    this.isLoadingDeleted = false;
                });
            })
            .catch(() => { runInAction(() => { this.isLoadingDeleted = false; }); });
    }

    restoreOrder = (id) => {
        http.post(`/user/orders/${id}/restore`)
            .then(() => {
                this.fetchOrdersDeleted();
            })
            .catch(error => {});
    }

    fetchOrders = () => {
        const requestId = ++this.fetchOrdersRequestId;
        const params = {
            page: this.page,
            limit: this.itemsPerPage
        };
        if (this.currentOrderStatus) {
            params.orderStatus = this.currentOrderStatus;
        }
        if (this.currentExpiryStatus) {
            params.expiryStatus = this.currentExpiryStatus;
        }
        if (this.searchType && this.searchText) {
            params.searchType = this.searchType;
            params.search = this.searchText;
        }
        this.isLoading = true;
        return http.get('/user/orders', { params })
            .then(response => {
                if (requestId !== this.fetchOrdersRequestId) return;
                runInAction(() => {
                    this.orders = response.data.orders;
                    if (response.data.pagination) {
                        this.pagination = {
                            currentPage: response.data.pagination.currentPage,
                            totalPages: response.data.pagination.totalPages,
                            totalItems: response.data.pagination.totalItems,
                            itemsPerPage: response.data.pagination.itemsPerPage,
                            hasNextPage: response.data.pagination.hasNextPage,
                            hasPrevPage: response.data.pagination.hasPrevPage
                        };
                    }
                    if (response.data.stats) {
                        this.stats = response.data.stats;
                    }
                    this.isLoading = false;
                });
            })
            .catch(() => {
                if (requestId !== this.fetchOrdersRequestId) return;
                runInAction(() => { this.isLoading = false; });
            });
    }

    setOrderStatusFilter = (status) => {
        this.currentOrderStatus = status;
        this.page = 1;
        this.fetchOrders();
    }

    setExpiryStatusFilter = (status) => {
        this.currentExpiryStatus = status;
        this.page = 1;
        this.fetchOrders();
    }

    setSearch = (type, text) => {
        this.searchType = type || null;
        this.searchText = text || null;
        this.page = 1;
        this.fetchOrders();
    }

    fetchSelection = (selectionId) => {
        return http.get(`/user/selection/${selectionId}`).then(response => {
            runInAction(() => {
                this.currentSelection = response.data?.selection;
                this.selectionPictures = response.data?.pictures || [];
                this.selectionTags = response.data?.tags || [];
            });
            return response.data?.order;
        });
    }

    createOrder = (data) => {
        return http.post('/user/order', data);
    }

    updateOrder = (orderId, data) => {
        return http.put(`/user/order/${orderId}`, data);
    }

    saveWatermark = (orderId, wm) => {
        const watermarkFields = {
            openWatermark: wm.enabled,
            watermark: wm.text,
            watermarkStyle: wm.style,
            watermarkPosition: wm.pos,
            watermarkFontSize: wm.size,
            watermarkAlpha: wm.alpha,
            watermarkRotate: wm.rotate,
            watermarkShadow: wm.shadow,
            watermarkInset: wm.inset,
        };
        return http.put(`/user/order/${orderId}`, watermarkFields).then(response => {
            const nextOrder = response.data?.order || response.data;
            runInAction(() => {
                if (nextOrder?.id) {
                    this.currentOrder = nextOrder;
                } else if (this.currentOrder?.id === orderId) {
                    this.currentOrder = { ...this.currentOrder, ...watermarkFields };
                }
            });
            return response;
        });
    }

    saveOrderTags = (orderId, tagIds) => {
        return http.put(`/user/order/${orderId}`, { tagIds }).then(response => {
            const nextOrder = response.data?.order || response.data;
            runInAction(() => {
                if (nextOrder?.id) {
                    this.currentOrder = {
                        ...this.currentOrder,
                        ...nextOrder,
                        tagDefinitions: nextOrder.tagDefinitions || this.currentOrder?.tagDefinitions || []
                    };
                }
            });
            return response;
        });
    }

    setAlbumCover = (albumId, cover) => {
        return http.put(`/user/albums/${albumId}/setAsCover`, { cover });
    }

    fetchQRCode = (orderId, pwd) => {
        const url = pwd
            ? `/user/order/${orderId}/qrCode?pwd=${pwd}`
            : `/user/order/${orderId}/qrCode`;
        return http.get(url).then(res => res.data.imageData);
    }

    setShare = (orderId, password, expire) => {
        return http.post(`/user/orders/${orderId}/setShare`, { password, expire });
    }

    setShareExpiryDays = (orderId, days) => {
        return http.patch(`/user/orders/${orderId}/share/expiry`, { days });
    }

    changeSharePassword = (orderId, password) => {
        return http.patch(`/user/orders/${orderId}/share/password`, { password });
    }

    cancelShare = (orderId) => {
        return http.get(`/user/orders/${orderId}/cancelShare`);
    }

    deleteOrder = (id) => {
        http.delete(`/user/order/${id}`).then(() => {
            this.fetchOrders();
        });
    }

    deletePermanent = (id) => {
        return http.delete(`/user/order/${id}/permanent`).then((response) => {
            // Releasing resources is irreversible. Hide the card immediately so
            // the user cannot race the asynchronous cleanup by clicking restore.
            this.ordersDeleted = this.ordersDeleted.filter(order => order.id !== id);
            return response;
        });
    }
}

const ordersStore = new OrdersStore();

export { ordersStore }
